import CircularProgress from "@material-ui/core/CircularProgress"
import { LinearProgress } from "@material-ui/core"
import { Elements, TextButton } from "@tfo/common"
import { useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"
import $http from "../../utils/$http"
import { usePolling } from "./usePolling"
import bank from '../../media/images/BankID_logo_white.png'

import "./BankIdSign.scss"

const SESSION_STATES = {
    Signed: "Signed",
    Expired: "Expired",
}

const { Paragraph, Heading1 } = Elements

export type BankIdSignProps = {
    orderId: number,
    onCancel: () => void
    onComplete: () => void
    onError: (message: string) => void
}

type SignResponseOtherDevice = {
    qrCodeBase64Encoded: string
    qrStartTime: Date
}

type SignResponseSameDevice = {
    launchUrl: string
    deviceMightRequireUserInteractionToLaunchBankIdApp: boolean
    deviceWillReloadPageOnReturnFromBankIdApp: boolean
}

type QrPromptProps = {
    qrCodeBase64: string
    qrStartTime: Date
}
interface QrPromptContainerProps extends QrPromptProps {
    toggleFullscreen: () => void
}

type QrCodeResponse = {
    qrCodeBase64Encoded: string
}

type CollectResponse = {
    status: CollectStatus
    statusMessage: string
    qrCodeBase64Encoded?: string
    qrStartTime?: Date
}

enum CollectStatus {
    Pending = "Pending",
    Complete = "Complete",
    Failure = "Failure",
    Retry = "Retry",
    Unknown = "Unknown",
}

const everySecond = 1000
const everyTwoSeconds = 2000

export const BankIdSign = ({ orderId, onCancel, onComplete, onError }: BankIdSignProps) => {
    const intl = useIntl()
    const bankIdIcon = () => <img className="bankId-icon" src={bank} alt="BankId Logo"></img>

    const isMobile = /Mobi|Android/i.test(navigator.userAgent)
    const returnUrl = `${window.location.origin}${window.location.pathname}#initiated=true`

    const [isStarting, setIsStarting] = useState<boolean>(true)
    const [qrCode, setQrCode] = useState<QrPromptProps | null>(null)
    const [polling, setPolling] = useState<{ qrInterval: number | null, collectInterval: number | null }>({ qrInterval: null, collectInterval: null })
    const [progressBar, setProgressBar] = useState<number>(100)
    const [progressTimer, setProgressTimer] = useState<string>("")
    const [ariaMessage, setAriaMessage] = useState<string>(intl.formatMessage({ id: "bankid.RFA18" }))
    const [statusMessage, setStatusMessage] = useState<string | null>(null)
    const [signOnSameDevice, setSignOnSameDevice] = useState<boolean>(isMobile)
    const [launchUrl, setLaunchUrl] = useState<string | null>(null)
    const [sessionStatus, setSessionStatus] = useState(SESSION_STATES.Signed)
    const [endTime, setEndTime] = useState<Date | null>(null)
    const [startTime, setStartTime] = useState<Date>(new Date())
    const [showExtendTimerButton, setShowExtendTimerButton] = useState<boolean>(false)
    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false)

    const messageRef = useRef<any>(null)
    const fullScreenRef = useRef(null)

    useEffect(() => {
        startTimer()
    }, [])

    const startTimer = () => {
        setShowExtendTimerButton(false)
        const now = new Date()
        const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
        setStartTime(now)
        setEndTime(fiveMinutesFromNow)
    }

    const restartSigning = () => {
        stopPolling()
        setQrCode(null)
        setStatusMessage(null)
        setIsStarting(true)
        setLaunchUrl(null)
        setSessionStatus(SESSION_STATES.Signed)
        setShowExtendTimerButton(false)
        startTimer()

        if (signOnSameDevice) {
            startSigningSameDevice(orderId, returnUrl)
        } else {
            startSigningOtherDevice(orderId, returnUrl)
        }
    }

    const isInitiated = new URLSearchParams(window.location.hash.slice(1)).get("initiated") === "true"

    const stopPolling = () => {
        setPolling({ qrInterval: null, collectInterval: null })
    }
    const startPolling = () => {
        setPolling({ qrInterval: everySecond, collectInterval: everyTwoSeconds })
    }

    usePolling(async () => {
        try {
            const { qrCodeBase64Encoded } = await $http.post<QrCodeResponse>(`bankid/${orderId}/qr-code`, { qrStartTime: qrCode?.qrStartTime })
            setQrCode((prev) => ({ ...prev, qrCodeBase64: qrCodeBase64Encoded }))
        } catch (error) {
            console.error("Failed to fetch QR code:", error)
        }
    }, polling.qrInterval)

    usePolling(async () => {
        try {
            const response = await $http.post<CollectResponse>(`bankid/${orderId}/collect`, { sameDevice: isMobile, endTime })
            switch (response.status) {
                case CollectStatus.Complete:
                    stopPolling()
                    onComplete()
                    setStatusMessage(null)
                    break;
                case CollectStatus.Failure:
                    stopPolling()
                    onError(response.statusMessage)
                    setStatusMessage(null)
                    setQrCode(null)
                    break;
                case CollectStatus.Retry:
                    stopPolling()
                    setStatusMessage(null)
                    setSessionStatus(SESSION_STATES.Expired)
                    setQrCode(null)
                    break;
                case CollectStatus.Unknown:
                    stopPolling()
                    onError(response.statusMessage)
                    setStatusMessage(null)
                    setQrCode(null)
                    break;
                case CollectStatus.Pending:
                    setStatusMessage(response.statusMessage)
                    if (response.qrCodeBase64Encoded) {
                        setQrCode({ qrCodeBase64: response.qrCodeBase64Encoded, qrStartTime: response.qrStartTime })
                    }
                    break;
            }
        } catch (error) {
            console.error("Failed to collect status:", error)
        }
    }, polling.collectInterval)

    const cancelSigning = async () => {
        try {
            await $http.post(`bankid/${orderId}/cancel`, {})
        } catch (error) {
            console.error("Failed to cancel signing:", error)
        }
        finally {
            stopPolling()
            onCancel()
        }
    }

    const startSigningOtherDevice = async (orderId: number, returnUrl: string) => {
        try {
            const { qrCodeBase64Encoded, qrStartTime } = await $http.post<SignResponseOtherDevice>(`bankid/${orderId}/start/other-device`, { returnUrl })

            setStatusMessage(statusMessage)
            setQrCode({ qrCodeBase64: qrCodeBase64Encoded, qrStartTime })
            startPolling()
        } catch (error) {
            console.error("Failed to start signing:", error)
        } finally {
            setIsStarting(false)
        }
    }

    const startSigningSameDevice = async (orderId: number, returnUrl: string) => {
        try {
            const { launchUrl, deviceMightRequireUserInteractionToLaunchBankIdApp, deviceWillReloadPageOnReturnFromBankIdApp } = await $http.post<SignResponseSameDevice>(`bankid/${orderId}/start/same-device`, { returnUrl })

            if (deviceMightRequireUserInteractionToLaunchBankIdApp) {
                setLaunchUrl(launchUrl)
            } else {
                window.location.replace(launchUrl)
            }

            startPolling()

        } catch (error) {
            console.error("Failed to start signing:", error)
        } finally {
            setIsStarting(false)
        }
    }

    const onSwitchDevice = () => {
        setSignOnSameDevice(false)
        startSigningOtherDevice(orderId, returnUrl)
    }

    useEffect(() => {
        if (!signOnSameDevice) {
            startSigningOtherDevice(orderId, returnUrl)
        } else {
            setIsStarting(false)
        }
    }, [orderId])

    useEffect(() => {
        if (qrCode && qrCode.qrStartTime) {
            progressCounter()
        }
    }, [qrCode])

    const checkTime = (time: number) => {
        if (time > 240) {
            const result = `5 ${intl.formatMessage({ id: 'startIndividual.bankid.progress.minutes' })}`
            setAriaMessage(result)
            return result
        } else if (time > 180) {
            const result = `4 ${intl.formatMessage({ id: 'startIndividual.bankid.progress.minutes' })}`
            setAriaMessage(result)
            return result
        } else if (time > 120) {
            const result = `3 ${intl.formatMessage({ id: 'startIndividual.bankid.progress.minutes' })}`
            setAriaMessage(result)
            return result
        } else if (time > 60) {
            const result = `2 ${intl.formatMessage({ id: 'startIndividual.bankid.progress.minutes' })}`
            setAriaMessage(result)
            return result
        } else if (time > 50) {
            const result = `1 ${intl.formatMessage({ id: 'startIndividual.bankid.progress.minute' })}`
            setAriaMessage(result)
            return result
        } else {
            return `${time} ${intl.formatMessage({ id: 'startIndividual.bankid.progress.seconds' })}`
        }
    }

    const progressCounter = () => {
        // This is for calculating the countdown timer for the QR code
        const timer = Math.max(Math.floor((endTime!.getTime() - new Date().getTime()) / 1000), 0)

        const timeLeft = checkTime(timer)
        setProgressTimer(timeLeft)

        if (timer === 50 || timer === 40 || timer === 30 || timer === 20 || timer === 10) {
            setAriaMessage(`${timer} ${intl.formatMessage({ id: 'startIndividual.bankid.progress.seconds' })}`)
        }

        if (timer <= 30) {
            setShowExtendTimerButton(true)
        }

        // This is for calculating the progress bar for the QR code
        const elapsed = Math.floor((new Date().getTime() - startTime.getTime()) / 1000)
        const total = (endTime!.getTime() - startTime.getTime()) / 1000
        const progress = Math.max(100 - (elapsed / total) * 100, 0)
        setProgressBar(progress)
    }

    useEffect(() => {
        function keyDownHandler(e: globalThis.KeyboardEvent) {
            if (e.key === " ") {
                e.preventDefault()
                restartSigning()
            }
        }

        window.addEventListener("keydown", keyDownHandler)

        return () => {
            window.removeEventListener("keydown", keyDownHandler)
        }
    }, [])

    useEffect(() => {
        showMessage()
    }, [statusMessage])

    function showMessage() {
        if (messageRef.current) {
            messageRef.current.focus();
        }
    }

    if (isInitiated && polling.collectInterval === null) {
        startPolling()
        setIsStarting(false)
    }

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            if (fullScreenRef.current.requestFullscreen) {
                fullScreenRef.current.requestFullscreen()
                setFullScreenMode(true)
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen()
                setFullScreenMode(false)
            }
        }
    }

    useEffect(() => {
        document.addEventListener('fullscreenchange', () => {
            setFullScreenMode(!!document.fullscreenElement)
        })

        return () => {
            document.removeEventListener('fullscreenchange', () => {
                setFullScreenMode(!!document.fullscreenElement)
            })
        }
    }, [])

    return (isStarting
        ? <div className='bankid-sign-container progressIcon'>
            <CircularProgress style={{ color: '#009B48' }} />
        </div>
        : <div className={'bankid-sign-container'} ref={fullScreenRef}>
            <div className="bankid-inner-content">
                {<Heading1 style={{ color: 'rgb(0, 155, 72)', fontSize: '2.4rem', lineHeight: '2.8rem', marginBottom: '1.5rem' }}>{intl.formatMessage({ id: "startIndividual.bankid.heading" })}</Heading1>}
                {signOnSameDevice ?
                    <>
                        <TextButton
                            className="btn-start"
                            onClick={() => startSigningSameDevice(orderId, returnUrl)}
                            icon={bankIdIcon()}
                            disabled={launchUrl !== null}>
                            {intl.formatMessage({
                                id: 'startIndividual.bankid.btn.start'
                            })}
                        </TextButton>
                        {launchUrl &&
                            <TextButton
                                className="btn-start"
                                onClick={() => window.location.replace(launchUrl)}>
                                {intl.formatMessage({ id: 'startIndividual.bankid.btn.openApp' })}
                            </TextButton>
                        }
                        <div className="bankid-sign-switch-device">
                            <Paragraph>{intl.formatMessage({ id: "startIndividual.bankid.otherDevice" })}</Paragraph>
                            <button className="link-button" onClick={() => onSwitchDevice()}>{intl.formatMessage({ id: "startIndividual.bankid.otherDevice.btn" })}</button>
                        </div>
                    </>
                    :
                    qrCode && sessionStatus !== SESSION_STATES.Expired &&

                    (<div className="bankid-qr-code-container">
                        <QRPrompt {...qrCode} toggleFullscreen={toggleFullscreen} />
                        <div className="bankid-timer-container">
                            <LinearProgress className="bankid-progress" aria-hidden variant="determinate" value={progressBar} />
                            <p aria-hidden>{progressTimer}</p>
                            <div
                                aria-live="assertive"
                                className="screen-reader-only"
                            >
                                {ariaMessage}
                            </div>
                            {showExtendTimerButton &&
                                <TextButton
                                    onClick={() => startTimer()}
                                    className="btn-extend"
                                    small
                                    secondary>
                                    {intl.formatMessage({ id: 'bankid.timeExtendButton' })}
                                </TextButton>}
                        </div>
                    </div>)
                }
                {statusMessage && <div className="bankid-sign-instruction">
                    <Paragraph tabIndex={-1} ref={messageRef} >{statusMessage}</Paragraph>
                </div>}
                {isMobile && !signOnSameDevice && <div className="bankid-sign-switch-device">
                    <Paragraph>{intl.formatMessage({ id: "startIndividual.bankid.sameDevice" })}</Paragraph>
                    <button className="link-button" onClick={() => onSwitchDevice()}>{intl.formatMessage({ id: "startIndividual.bankid.sameDevice.btn" })}</button>
                </div>
                }

                {sessionStatus === SESSION_STATES.Expired &&
                    <TextButton
                        onClick={() => restartSigning()}
                        className="bankid-sign-switch-device"
                        secondary>
                        {intl.formatMessage({ id: 'bankid.tryAgainButton' })}
                    </TextButton>
                }
                <div className="bankid-sign-information">
                    <Paragraph style={{ fontSize: '14px', lineHeight: '2.1rem' }}>{intl.formatMessage({ id: "startIndividual.bankid.information" })}</Paragraph>
                </div>
                {onCancel && !fullScreenMode ?
                    <TextButton
                        secondary
                        className="btn-cancel"
                        onClick={cancelSigning}>
                        {intl.formatMessage({
                            id: 'startIndividual.bankid.btn.cancel',
                        })}
                    </TextButton> :
                    <TextButton
                        secondary
                        className="btn-leave-fullscreen"
                        onClick={e => toggleFullscreen()}>
                        {intl.formatMessage({
                            id: 'startIndividual.bankid.btn.leaveFullscreen',
                        })}
                    </TextButton>}
            </div>
        </div>)
}

export const QRPrompt: React.FC<QrPromptContainerProps> = ({ qrCodeBase64, toggleFullscreen }) => {
    return (
        <>
            {qrCodeBase64 &&
                <div style={{ display: 'flex', justifyContent: 'center' }} >
                    <button style={{
                        background: "none",
                        border: 0,
                        padding: 0,
                        margin: 0,
                        font: "inherit",
                        color: "inherit",
                        lineHeight: "inherit",
                        textAlign: "inherit",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        cursor: "pointer"
                    }}
                        onClick={e => toggleFullscreen()}><img style={{ width: '20rem', height: '20rem' }} src={'data:image/png;base64, ' + qrCodeBase64} alt="QR Code" ></img>
                    </button>
                </div>

            }
        </>
    )
}